import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { FETCH_LIGHTSPEED_LOCATIONS, GET_LIGHTSPEED_STORES } from '../queries'
import { LightspeedBusiness, LightspeedStore } from '../types'
import { PARTNER_SLUGS } from '../../../constants'
import { useSession } from 'packages/@slerp/accounts'

const LIGHTSPEED_PATH = `/integrations/${PARTNER_SLUGS.LIGHTSPEED}`

export const useLocationPageData = () => {
  const { storeId: storeIdParam } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const isNewConnection = storeIdParam === 'connect'

  const { merchant } = useSession()

  const [businesses, setBusinesses] = useState<LightspeedBusiness[]>([])
  const [connectedLocations, setConnectedLocations] = useState<
    LightspeedStore[]
  >([])
  const [businessesLoaded, setBusinessesLoaded] = useState(false)
  const [storesLoaded, setStoresLoaded] = useState(false)

  useQuery(FETCH_LIGHTSPEED_LOCATIONS, {
    onCompleted: (data) => {
      setBusinesses(data.fetchLightspeedIds)
      setBusinessesLoaded(true)
    },
    onError: () => {
      navigate('/integrations')
    }
  })

  useQuery(GET_LIGHTSPEED_STORES, {
    fetchPolicy: 'no-cache',
    variables: { merchantId: merchant?.id },
    onCompleted: (data) => {
      const connected = data?.stores.filter(
        (store: LightspeedStore) =>
          !!store.store_integration_settings.lightspeed_business_id
      )
      setConnectedLocations(connected || [])
      setStoresLoaded(true)
    }
  })

  const selectedBusiness = useMemo<LightspeedBusiness | null>(() => {
    if (!businesses.length) return null

    if (isNewConnection) {
      const businessId = searchParams.get('businessId')
      return businesses.find((b) => String(b.id) === businessId) || null
    }

    const location = connectedLocations.find((l) => l.id === storeIdParam)
    if (location) {
      return (
        businesses.find(
          (b) =>
            String(b.id) ===
            String(location.store_integration_settings.lightspeed_business_id)
        ) || null
      )
    }

    return null
  }, [
    businesses,
    connectedLocations,
    storeIdParam,
    isNewConnection,
    searchParams
  ])

  const selectedLocation = useMemo<LightspeedStore | null>(() => {
    if (isNewConnection) return null
    return connectedLocations.find((l) => l.id === storeIdParam) || null
  }, [connectedLocations, storeIdParam, isNewConnection])

  useEffect(() => {
    if (businessesLoaded && storesLoaded && !selectedBusiness) {
      navigate(LIGHTSPEED_PATH, { replace: true })
    }
  }, [businessesLoaded, storesLoaded, selectedBusiness])

  const isLoading = !businessesLoaded || !storesLoaded

  return {
    selectedBusiness,
    selectedLocation,
    connectedLocations,
    isNewConnection,
    isLoading
  }
}
